"use client";

import { startTransition } from "react";
import { Check, Languages } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

import { setLocale } from "@/app/actions/set-locale";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { locales, type Locale } from "@/i18n/config";

export function LocaleSwitcher({
  variant = "default",
}: {
  variant?: "default" | "rail";
}) {
  const currentLocale = useLocale();
  const router = useRouter();
  const t = useTranslations("foundation.language");

  function selectLocale(locale: Locale) {
    if (locale === currentLocale) return;

    startTransition(async () => {
      await setLocale(locale);
      router.refresh();
    });
  }

  if (variant === "rail") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" mode="icon" aria-label={t("label")}>
            <Languages className="size-4.5!" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" sideOffset={8}>
          <DropdownMenuLabel>{t("label")}</DropdownMenuLabel>
          {locales.map((locale) => (
            <DropdownMenuItem
              key={locale}
              disabled={locale === currentLocale}
              onSelect={() => selectLocale(locale)}
            >
              <span>{t(locale)}</span>
              {locale === currentLocale ? (
                <Check className="ms-auto size-4" aria-hidden="true" />
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex gap-2" role="group" aria-label={t("label")}>
      {locales.map((locale) => {
        const selected = locale === currentLocale;

        return (
          <button
            key={locale}
            type="button"
            aria-pressed={selected}
            onClick={() => selectLocale(locale)}
            className="min-h-8 rounded-md border border-border bg-card px-3 py-1 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:bg-primary disabled:text-primary-foreground"
            disabled={selected}
          >
            {t(locale)}
          </button>
        );
      })}
    </div>
  );
}
